import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { ROLES } from '../../data/roles';
import { dataFetch, authFetch } from '../../api';
import PageHeader from '../../components/ui/PageHeader';
import AdminInvitesTab from './AdminInvitesTab';

const { FiShield, FiUsers, FiUser, FiSettings, FiCheck, FiX, FiLoader, FiLock, FiAlertCircle, FiMail } = FiIcons;

const roleColors = {
  [ROLES.ADMINISTRATOR]:    { bar: 'bg-red-500',    icon: 'bg-red-50 text-red-600',    badge: 'bg-red-50 text-red-700 border-red-200'    },
  [ROLES.IT_SECURITY_ADMIN]:{ bar: 'bg-blue-500',   icon: 'bg-blue-50 text-blue-600',   badge: 'bg-blue-50 text-blue-700 border-blue-200'   },
  [ROLES.MANAGER]:          { bar: 'bg-amber-500',  icon: 'bg-amber-50 text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  [ROLES.EMPLOYEE]:         { bar: 'bg-emerald-500',icon: 'bg-emerald-50 text-emerald-600', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};
const roleIcons = { [ROLES.ADMINISTRATOR]: FiShield, [ROLES.IT_SECURITY_ADMIN]: FiSettings, [ROLES.MANAGER]: FiUsers, [ROLES.EMPLOYEE]: FiUser };

const fmt = p => p.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const UserRolesTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Reset Password Modal States
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    dataFetch('read/employees?limit=500').then(res => {
      if (res?.status === 'success') setUsers(res.data || []);
      setLoading(false);
    });
  }, []);

  const handleRoleChange = async (userId, newRole) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    const userToUpdate = users.find(u => u.id === userId);
    if (!userToUpdate) return;
    await dataFetch(`update/employees/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ ...userToUpdate, role: newRole })
    });
  };

  const openResetModal = (user) => {
    setUserToReset(user);
    setResetError('');
    setResetSuccess(false);
    setResetModalOpen(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess(false);
    setResetLoading(true);

    try {
      const authRes = await authFetch('forget-password', {
        method: 'POST',
        body: JSON.stringify({ email: userToReset.email })
      });

      if (authRes?.status === 'error') {
        setResetError(authRes.error || 'Failed to send password reset email.');
        setResetLoading(false);
        return;
      }
      
      setResetSuccess(true);
      setTimeout(() => setResetModalOpen(false), 2500);
    } catch (err) {
      setResetError('Failed to send password reset link. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-6 relative">
      <div className="px-6 py-4 border-b border-slate-200">
        <h3 className="text-lg font-bold text-slate-800">User Roles</h3>
        <p className="text-sm text-slate-500">Change roles for existing employees and managers.</p>
      </div>
      <div className="p-0 overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase">
            <tr>
              <th className="px-6 py-4">Employee</th>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Current Role</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {loading ? (
              <tr><td colSpan="4" className="text-center py-12 text-slate-500"><SafeIcon icon={FiLoader} className="animate-spin text-2xl mx-auto mb-2" /> Loading users...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="4" className="text-center py-12 text-slate-500">No users found.</td></tr>
            ) : users.map(u => (
              <tr key={u.id} className="hover:bg-slate-50">
                <td className="px-6 py-4 font-medium text-slate-900">{u.firstName} {u.lastName}</td>
                <td className="px-6 py-4 text-slate-600">{u.email}</td>
                <td className="px-6 py-4">
                   <span className="px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-md text-xs font-medium text-slate-700">
                     {u.role || ROLES.EMPLOYEE}
                   </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-3">
                    <select
                      value={u.role || ROLES.EMPLOYEE}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-slate-400 bg-white shadow-sm cursor-pointer"
                    >
                      {Object.values(ROLES).map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button
                      onClick={() => openResetModal(u)}
                      className="p-1.5 text-slate-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                      title="Send Password Reset Link"
                    >
                      <SafeIcon icon={FiMail} className="text-base" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {resetModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <SafeIcon icon={FiMail} className="text-blue-600" />
                  Send Password Reset Link
                </h3>
                <button onClick={() => setResetModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>
              <div className="p-6">
                {resetSuccess ? (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={FiCheck} className="text-3xl text-emerald-600" />
                    </div>
                    <h4 className="text-lg font-bold text-slate-800 mb-2">Reset Link Sent</h4>
                    <p className="text-slate-500 text-sm">A password reset link has been successfully sent to {userToReset?.email}.</p>
                  </div>
                ) : (
                  <form onSubmit={handleResetPassword}>
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 mb-6 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Name:</span>
                        <span className="font-medium text-slate-800">{userToReset?.firstName} {userToReset?.lastName}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Email:</span>
                        <span className="font-medium text-slate-800 truncate max-w-[200px]">{userToReset?.email}</span>
                      </div>
                    </div>

                    <p className="text-sm text-slate-600 mb-6">
                      Are you sure you want to send a password reset link to this user? They will receive an email with instructions to create a new password securely.
                    </p>

                    {resetError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
                        <SafeIcon icon={FiAlertCircle} className="text-red-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-red-700">{resetError}</p>
                      </div>
                    )}

                    <div className="mt-6 flex justify-end gap-3">
                      <button type="button" onClick={() => setResetModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                      <button type="submit" disabled={resetLoading} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                        {resetLoading ? <><SafeIcon icon={FiLoader} className="animate-spin" /> Sending...</> : 'Send Reset Link'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RoleManagement = () => {
  const [activeTab, setActiveTab] = useState('permissions');
  const [selected, setSelected] = useState(ROLES.ADMINISTRATOR);
  
  const [permissionsData, setPermissionsData] = useState([]);
  const [loadingPerms, setLoadingPerms] = useState(true);
  const [updatingPerm, setUpdatingPerm] = useState(null);
  
  const [showHidden, setShowHidden] = useState(false);
  const [addPermModal, setAddPermModal] = useState(false);
  const [newPermGroup, setNewPermGroup] = useState('Custom Permissions');
  const [newPermLabel, setNewPermLabel] = useState('');
  const [editPermModal, setEditPermModal] = useState(null);

  const fetchPermissions = async () => {
    const res = await dataFetch('read/roles_permissions_1740000000?limit=1000');
    if (res?.status === 'success') {
      setPermissionsData(res.data || []);
    }
    setLoadingPerms(false);
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const allPermissionsMap = new Map();
  const permissionStatusMap = new Map();

  permissionsData.forEach(p => {
    if (!allPermissionsMap.has(p.permission_key)) {
      allPermissionsMap.set(p.permission_key, {
        key: p.permission_key,
        label: p.permission_label || fmt(p.permission_key),
        group: p.group_name
      });
      permissionStatusMap.set(p.permission_key, false);
    }
  });

  allPermissionsMap.forEach((perm, key) => {
    const rows = permissionsData.filter(r => r.permission_key === key);
    const isGloballyHidden = rows.every(r => r.is_active === false);
    permissionStatusMap.set(key, isGloballyHidden);
  });

  let allPermissions = Array.from(allPermissionsMap.values());
  if (!showHidden) {
    allPermissions = allPermissions.filter(perm => !permissionStatusMap.get(perm.key));
  }

  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    if (!acc[perm.group]) acc[perm.group] = [];
    acc[perm.group].push(perm);
    return acc;
  }, {});

  const selectedRolePerms = permissionsData.filter(p => p.role_name === selected && p.is_active !== false).map(p => p.permission_key);

  const getRoleCount = (role) => {
    return permissionsData.filter(p => p.role_name === role && p.is_active !== false).length;
  };

  const togglePermission = async (permKey, groupName, permLabel) => {
    if (updatingPerm) return;
    setUpdatingPerm(permKey);
    const isGranted = selectedRolePerms.includes(permKey);
    const row = permissionsData.find(p => p.role_name === selected && p.permission_key === permKey);
    
    try {
      if (isGranted && row) {
        await dataFetch(`update/roles_permissions_1740000000/${row.id}`, { method: 'PUT', body: JSON.stringify({ ...row, is_active: false }) });
      } else {
        if (row) {
          await dataFetch(`update/roles_permissions_1740000000/${row.id}`, { method: 'PUT', body: JSON.stringify({ ...row, is_active: true }) });
        } else {
          await dataFetch('create/roles_permissions_1740000000', {
            method: 'POST',
            body: JSON.stringify({ role_name: selected, permission_key: permKey, permission_label: permLabel, group_name: groupName, is_active: true })
          });
        }
      }
      await fetchPermissions();
    } finally {
      setUpdatingPerm(null);
    }
  };

  const handleAddPermission = async (e) => {
    e.preventDefault();
    if (!newPermLabel.trim()) return;
    const key = newPermLabel.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    await dataFetch('create/roles_permissions_1740000000', {
      method: 'POST',
      body: JSON.stringify({
         role_name: selected, 
         permission_key: key,
         permission_label: newPermLabel,
         group_name: newPermGroup,
         is_active: true
      })
    });
    setAddPermModal(false);
    setNewPermLabel('');
    fetchPermissions();
  };

  const submitEditPermission = async (e) => {
    e.preventDefault();
    const rows = permissionsData.filter(p => p.permission_key === editPermModal.key);
    
    if (rows.length > 0) {
      const records = rows.map(r => ({ ...r, permission_label: editPermModal.label, group_name: editPermModal.group }));
      await dataFetch('bulk/update/roles_permissions_1740000000', { method: 'PUT', body: JSON.stringify({ records }) });
    }
    setEditPermModal(null);
    fetchPermissions();
  };

  const hidePermissionGlobally = async (permKey) => {
    if(!window.confirm('Are you sure you want to hide this permission? It will be removed from all roles.')) return;
    const rows = permissionsData.filter(p => p.permission_key === permKey && p.is_active !== false);
    
    if (rows.length > 0) {
      const records = rows.map(r => ({ ...r, is_active: false }));
      await dataFetch('bulk/update/roles_permissions_1740000000', { method: 'PUT', body: JSON.stringify({ records }) });
    }
    fetchPermissions();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-[1400px] mx-auto">
      <PageHeader eyebrow="Admin" title="Role Management" description="Manage roles, permissions, and administrator invitations." />

      <div className="flex gap-4 border-b border-slate-200 mb-6">
        <button
          onClick={() => setActiveTab('permissions')}
          className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'permissions' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Role Permissions
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'users' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          User Roles
        </button>
        <button
          onClick={() => setActiveTab('invitations')}
          className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'invitations' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Admin Invitations
        </button>
      </div>

      {activeTab === 'permissions' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.values(ROLES).map(role => {
              const colors = roleColors[role];
              const Icon = roleIcons[role];
              const count = getRoleCount(role);
              const totalActive = allPermissions.length;
              const isSelected = selected === role;
              return (
                <button key={role} onClick={() => setSelected(role)}
                  className={`text-left p-5 rounded-xl border transition-all shadow-sm bg-white ${isSelected ? 'border-[#0f172a] shadow-md' : 'border-slate-200 hover:border-slate-300'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors.icon}`}>
                      <SafeIcon icon={Icon} className="text-base" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{role}</p>
                      <p className="text-[10px] text-slate-400">{count}/{Math.max(count, totalActive)} permissions</p>
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full ${colors.bar}`} style={{ width: `${(count / Math.max(1, totalActive)) * 100}%` }} />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-6">
            <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap gap-4 items-center justify-between">
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Selected Role</p>
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-slate-900">{selected}</h3>
                  <span className={`px-2.5 py-0.5 rounded-md text-xs font-semibold border ${roleColors[selected].badge}`}>{selectedRolePerms.length} assigned</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-slate-900 select-none">
                  <input type="checkbox" checked={showHidden} onChange={e => setShowHidden(e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-900" />
                  Show Hidden
                </label>
                <button 
                  onClick={() => setAddPermModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors shadow-sm"
                >
                  <SafeIcon icon={FiIcons.FiPlus} />
                  Add Permission
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-8 min-h-[300px]">
              {loadingPerms ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <SafeIcon icon={FiLoader} className="animate-spin text-3xl mb-3" />
                  <p className="text-sm">Loading permissions...</p>
                </div>
              ) : Object.keys(groupedPermissions).length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <SafeIcon icon={FiShield} className="text-4xl mx-auto mb-3 opacity-20" />
                  <p>No permissions found.</p>
                </div>
              ) : (
                Object.keys(groupedPermissions).map(groupName => (
                  <div key={groupName}>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 pl-1">{groupName}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {groupedPermissions[groupName].map(perm => {
                        const granted = selectedRolePerms.includes(perm.key);
                        const isHidden = permissionStatusMap.get(perm.key);
                        
                        return (
                          <div 
                            key={perm.key} 
                            className={`group relative flex items-center justify-between px-3 py-2.5 rounded-lg border text-xs font-medium cursor-pointer transition-all ${granted ? 'bg-emerald-50 border-emerald-200 text-emerald-800 shadow-sm' : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300'} ${isHidden ? 'opacity-50 grayscale hover:opacity-100' : ''}`}
                            onClick={() => togglePermission(perm.key, perm.group, perm.label)}
                          >
                            <div className="flex items-center gap-2.5 pr-2">
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${granted ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                                {updatingPerm === perm.key ? (
                                  <SafeIcon icon={FiLoader} className="animate-spin text-white text-[9px]" />
                                ) : (
                                  <SafeIcon icon={granted ? FiCheck : FiIcons.FiMinus} className="text-white text-[9px]" />
                                )}
                              </div>
                              <span className={`truncate ${isHidden ? 'line-through decoration-slate-400' : ''}`}>{perm.label}</span>
                            </div>
                            
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                              <button 
                                onClick={(e) => { e.stopPropagation(); setEditPermModal(perm); }}
                                className="p-1.5 text-slate-400 hover:text-blue-600 rounded bg-white shadow-sm border border-slate-200"
                                title="Edit Permission"
                              >
                                <SafeIcon icon={FiIcons.FiEdit2} className="text-[10px]" />
                              </button>
                              {!isHidden && (
                                <button 
                                  onClick={(e) => { e.stopPropagation(); hidePermissionGlobally(perm.key); }}
                                  className="p-1.5 text-slate-400 hover:text-red-600 rounded bg-white shadow-sm border border-slate-200"
                                  title="Hide Permission"
                                >
                                  <SafeIcon icon={FiIcons.FiTrash2} className="text-[10px]" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'users' && <UserRolesTab />}
      {activeTab === 'invitations' && <AdminInvitesTab />}

      {/* Add Permission Modal */}
      <AnimatePresence>
        {addPermModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiPlusSquare} className="text-blue-600" />
                  Add New Permission
                </h3>
                <button onClick={() => setAddPermModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors"><SafeIcon icon={FiX} className="text-xl" /></button>
              </div>
              <div className="p-6">
                <form onSubmit={handleAddPermission} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Group Name</label>
                    <input type="text" value={newPermGroup} onChange={(e) => setNewPermGroup(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-all text-sm" placeholder="e.g. Compliance, Dashboard" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Permission Label</label>
                    <input type="text" value={newPermLabel} onChange={(e) => setNewPermLabel(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-all text-sm" placeholder="e.g. View Secret Logs" required />
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button type="button" onClick={() => setAddPermModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                    <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors shadow-sm">Add Permission</button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Permission Modal */}
      <AnimatePresence>
        {editPermModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiEdit2} className="text-blue-600" />
                  Edit Permission
                </h3>
                <button onClick={() => setEditPermModal(null)} className="text-slate-400 hover:text-slate-600 transition-colors"><SafeIcon icon={FiX} className="text-xl" /></button>
              </div>
              <div className="p-6">
                <form onSubmit={submitEditPermission} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Group Name</label>
                    <input type="text" value={editPermModal.group} onChange={(e) => setEditPermModal({...editPermModal, group: e.target.value})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-all text-sm" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Permission Label</label>
                    <input type="text" value={editPermModal.label} onChange={(e) => setEditPermModal({...editPermModal, label: e.target.value})} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-all text-sm" required />
                  </div>
                  <div className="text-xs text-slate-500">
                    <SafeIcon icon={FiAlertCircle} className="inline mr-1" />
                    Changes will apply to this permission across all roles.
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button type="button" onClick={() => setEditPermModal(null)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                    <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors shadow-sm">Save Changes</button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default RoleManagement;